import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchDestination, fetchPhotosByRegion, photoUrl } from "@/lib/api";
import { RouteCard } from "@/components/RouteCard";
import { RotatingHero } from "@/components/RotatingHero";
import { ArrowLeft } from "lucide-react";

export default function DestinationDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [regionPhotos, setRegionPhotos] = useState([]);

    useEffect(() => {
        setData(null);
        setNotFound(false);
        setRegionPhotos([]);
        fetchDestination(slug)
            .then((d) => {
                setData(d);
                document.title = `${d.destination.name} · Destination Guide`;
            })
            .catch(() => setNotFound(true));
        fetchPhotosByRegion(slug)
            .then((ps) => setRegionPhotos(ps.map((p) => photoUrl(p.url))))
            .catch(() => {});
    }, [slug]);

    if (notFound) {
        return (
            <div data-testid="destination-not-found" className="min-h-screen flex items-center justify-center px-6 pt-40 text-center">
                <div>
                    <div className="eyebrow">404</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl">Destination not found</h1>
                    <Link to="/destinations" className="mt-8 inline-block text-[#E87722] font-display uppercase tracking-[0.2em]">← All destinations</Link>
                </div>
            </div>
        );
    }

    if (!data) return <div data-testid="destination-loading" className="min-h-screen flex items-center justify-center text-[var(--fg-dim)] font-body">Loading…</div>;
    const { destination, routes } = data;

    return (
        <div data-testid="destination-detail-page">
            <section className="relative min-h-[70vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <RotatingHero
                        images={regionPhotos}
                        fallback={destination.hero_image}
                        className="absolute inset-0"
                        intervalMs={7500}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-[#14161A]/70 to-[#14161A]/20" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-16 w-full on-hero">
                    <Link to="/destinations" className="inline-flex items-center gap-2 text-[var(--fg-muted)] hover:text-[#E87722] font-display uppercase tracking-[0.2em] text-xs mb-8">
                        <ArrowLeft size={14} /> All Destinations
                    </Link>
                    <div className="eyebrow">{destination.tagline}</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tighter">
                        {destination.name}
                    </h1>
                    <p className="mt-8 font-body text-[var(--fg-muted)] text-lg md:text-xl max-w-3xl leading-relaxed">
                        {destination.description}
                    </p>
                </div>
            </section>

            <section className="py-16 lg:py-24 border-t border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-3">Roads in {destination.name}</div>
                    <h2 className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-6xl leading-none tracking-tight mb-12">
                        The rides.
                    </h2>
                    {routes.length === 0 ? (
                        <p className="font-body text-[var(--fg-dim)]">More routes coming to this region soon.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {routes.map((r, i) => <RouteCard key={r.slug} route={r} index={i} />)}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
